import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { HazardType, Incident, IncidentStatus, Page, ResourceType, Tasking, TaskingStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const EOC_ROLES = ['DUTY_OFFICER', 'EOC_COMMANDER'];
const HAZARDS: HazardType[] = ['CYCLONE', 'FLOOD', 'TSUNAMI', 'LANDSLIDE', 'MARINE', 'HEALTH'];
const INCIDENT_STATUSES: IncidentStatus[] = ['OPEN', 'CONTAINED', 'CLOSED'];
const RESOURCE_TYPES: ResourceType[] = ['PERSONNEL', 'VEHICLES', 'BOATS', 'AIRCRAFT', 'MEDICAL', 'SHELTER_SUPPLIES', 'ENGINEERING', 'OTHER'];
const TASKING_STATUSES: TaskingStatus[] = ['ASSIGNED', 'ACKNOWLEDGED', 'IN_PROGRESS', 'COMPLETE', 'CANCELLED'];
const NEXT_TASKING_STATUS: Record<TaskingStatus, TaskingStatus[]> = {
  ASSIGNED: ['ACKNOWLEDGED', 'CANCELLED'],
  ACKNOWLEDGED: ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['COMPLETE', 'CANCELLED'],
  COMPLETE: [],
  CANCELLED: [],
};

type Tab = 'incidents' | 'taskings';

/** EOC IncidentBoard: incidents + agency taskings + sitreps + resource requests, all cross-linked (M36). */
export function IncidentBoard() {
  const { persona } = useAuth();
  const isEoc = persona ? persona.roles.some((r) => EOC_ROLES.includes(r)) : false;
  const [tab, setTab] = useState<Tab>('incidents');

  return (
    <div>
      <PageHeader title="EOC Incident Board" subtitle="Incidents, agency taskings, situation reports, and resource requests (M36)." />
      <TabStrip tabs={[{ key: 'incidents', label: 'Incidents' }, { key: 'taskings', label: 'Cross-incident tasking worklist' }]} active={tab} onChange={setTab} />
      {!isEoc ? <RoleNotice roles={EOC_ROLES} /> : null}
      {tab === 'incidents' ? <IncidentsTab isEoc={isEoc} /> : <TaskingsTab isEoc={isEoc} />}
    </div>
  );
}

function IncidentsTab({ isEoc }: { isEoc: boolean }) {
  const [status, setStatus] = useState('');
  const [hazard, setHazard] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (hazard) params.set('hazard', hazard);
    return `/api/v1/m36/incidents?${params.toString()}`;
  }, [status, hazard]);
  const listQ = useApiQuery<Page<Incident>>(isEoc ? path : null);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <select className="field-input !w-auto" value={hazard} onChange={(e) => setHazard(e.target.value)}>
            <option value="">All hazards</option>
            {HAZARDS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {INCIDENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        {isEoc ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Open incident
          </button>
        ) : null}
      </div>

      {isEoc ? (
        <>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No incidents match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Title</th>
                    <th className="table-head">Hazard</th>
                    <th className="table-head">Districts</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((i) => (
                    <tr key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(i.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{i.refNo}</td>
                      <td className="table-cell">{i.title}</td>
                      <td className="table-cell">
                        <StatusBadge status={i.hazard} />
                      </td>
                      <td className="table-cell text-xs text-slate-500">{i.districts.length === 0 ? 'ALL' : i.districts.join(', ')}</td>
                      <td className="table-cell">
                        <StatusBadge status={i.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? (
        <IncidentDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateIncidentModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function IncidentDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<Incident>(`/api/v1/m36/incidents/${id}`);
  const i = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closedNotes, setClosedNotes] = useState('');
  const [narrative, setNarrative] = useState('');
  const [agency, setAgency] = useState('');
  const [instructions, setInstructions] = useState('');
  const [resourceType, setResourceType] = useState<ResourceType>('PERSONNEL');
  const [resourceDescription, setResourceDescription] = useState('');
  const [resourceQty, setResourceQty] = useState('1');
  const [denialReasonByReq, setDenialReasonByReq] = useState<Record<string, string>>({});

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const resourceNext: Record<string, string[]> = { REQUESTED: ['APPROVED', 'DENIED'], APPROVED: ['FULFILLED', 'DENIED'], FULFILLED: [], DENIED: [] };

  return (
    <Modal title={i ? `${i.refNo} — ${i.title}` : 'Incident'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {i ? (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={i.status} />} />
            <Detail label="Hazard" value={<StatusBadge status={i.hazard} />} />
            <Detail label="Districts" value={i.districts.length === 0 ? 'ALL' : i.districts.join(', ')} />
            {i.commanderId ? <Detail label="Commander" value={i.commanderId} mono /> : null}
            {i.alertId ? <Detail label="Linked alert" value={i.alertId} mono /> : null}
          </div>
          <p className="text-slate-700">{i.summary}</p>
          {i.closedNotes ? <p className="text-xs text-slate-500">Closed notes: {i.closedNotes}</p> : null}

          {i.status === 'OPEN' ? (
            <button className="btn-secondary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m36/incidents/${id}/contain`))}>
              Mark contained
            </button>
          ) : null}
          {i.status !== 'CLOSED' ? (
            <div className="flex gap-2">
              <input className="field-input" placeholder="Closing notes (optional)" value={closedNotes} onChange={(e) => setClosedNotes(e.target.value)} />
              <button className="btn-danger" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m36/incidents/${id}/close`, { closedNotes: closedNotes || undefined }))}>
                Close incident
              </button>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <div className="mb-2 text-sm font-bold text-slate-700">Taskings ({i.taskings?.length ?? 0})</div>
              {i.status !== 'CLOSED' ? (
                <div className="mb-2 space-y-1.5 rounded-lg border border-slate-200 p-2.5">
                  <input className="field-input" placeholder="Agency, e.g. SFRA, Coast Guard" value={agency} onChange={(e) => setAgency(e.target.value)} />
                  <input className="field-input" placeholder="Instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} />
                  <button
                    className="btn-secondary"
                    disabled={busy || agency.trim().length < 2 || instructions.trim().length < 3}
                    onClick={() =>
                      act(async () => {
                        await api.post(`/api/v1/m36/incidents/${id}/taskings`, { agency, instructions });
                        setAgency('');
                        setInstructions('');
                      })
                    }
                  >
                    Assign tasking
                  </button>
                </div>
              ) : null}
              <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {(i.taskings ?? []).length === 0 ? (
                  <p className="text-xs text-slate-400">No taskings assigned.</p>
                ) : (
                  i.taskings!.map((t) => <TaskingRow key={t.id} t={t} busy={busy} onAdvance={(status) => act(() => api.post(`/api/v1/m36/taskings/${t.id}/status`, { status }))} />)
                )}
              </div>
            </div>

            <div>
              <div className="mb-2 text-sm font-bold text-slate-700">Resource requests ({i.resourceRequests?.length ?? 0})</div>
              <div className="mb-2 flex gap-1.5 rounded-lg border border-slate-200 p-2.5">
                <select className="field-input !w-auto" value={resourceType} onChange={(e) => setResourceType(e.target.value as ResourceType)}>
                  {RESOURCE_TYPES.map((rt) => (
                    <option key={rt} value={rt}>
                      {rt}
                    </option>
                  ))}
                </select>
                <input className="field-input" placeholder="Description" value={resourceDescription} onChange={(e) => setResourceDescription(e.target.value)} />
                <input className="field-input !w-16" type="number" min="1" value={resourceQty} onChange={(e) => setResourceQty(e.target.value)} />
                <button
                  className="btn-secondary"
                  disabled={busy || resourceDescription.trim().length < 3}
                  onClick={() =>
                    act(async () => {
                      await api.post(`/api/v1/m36/incidents/${id}/resource-requests`, {
                        resourceType,
                        description: resourceDescription,
                        quantity: Number.parseInt(resourceQty, 10) || 1,
                      });
                      setResourceDescription('');
                    })
                  }
                >
                  Request
                </button>
              </div>
              <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {(i.resourceRequests ?? []).length === 0 ? (
                  <p className="text-xs text-slate-400">No resource requests.</p>
                ) : (
                  i.resourceRequests!.map((r) => (
                    <div key={r.id} className="rounded border border-slate-100 px-2.5 py-1.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span>
                          <StatusBadge status={r.resourceType} /> ×{r.quantity} — {r.description}
                        </span>
                        <StatusBadge status={r.status} />
                      </div>
                      {(resourceNext[r.status] ?? []).length > 0 ? (
                        <div className="mt-1 flex items-center gap-1">
                          {(resourceNext[r.status] ?? []).map((d) =>
                            d === 'DENIED' ? (
                              <span key={d} className="flex items-center gap-1">
                                <input
                                  className="field-input !w-28 !py-0.5 text-[11px]"
                                  placeholder="Denial reason"
                                  value={denialReasonByReq[r.id] ?? ''}
                                  onChange={(e) => setDenialReasonByReq((m) => ({ ...m, [r.id]: e.target.value }))}
                                />
                                <button
                                  className="btn-danger !px-1.5 !py-0.5 text-[11px]"
                                  disabled={busy || !(denialReasonByReq[r.id] ?? '').trim()}
                                  onClick={() => act(() => api.post(`/api/v1/m36/resource-requests/${r.id}/decision`, { decision: 'DENIED', denialReason: denialReasonByReq[r.id] }))}
                                >
                                  Deny
                                </button>
                              </span>
                            ) : (
                              <button key={d} className="btn-secondary !px-1.5 !py-0.5 text-[11px]" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m36/resource-requests/${r.id}/decision`, { decision: d }))}>
                                {d}
                              </button>
                            ),
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-bold text-slate-700">Situation reports ({i.sitreps?.length ?? 0})</div>
            <div className="mb-2 flex gap-1.5">
              <textarea className="field-input" rows={2} placeholder="Narrative" value={narrative} onChange={(e) => setNarrative(e.target.value)} />
              <button
                className="btn-secondary"
                disabled={busy || narrative.trim().length < 3}
                onClick={() =>
                  act(async () => {
                    await api.post(`/api/v1/m36/incidents/${id}/sitreps`, { narrative });
                    setNarrative('');
                  })
                }
              >
                File sitrep
              </button>
            </div>
            <div className="max-h-56 space-y-1.5 overflow-y-auto">
              {(i.sitreps ?? []).length === 0 ? (
                <p className="text-xs text-slate-400">No sitreps filed.</p>
              ) : (
                i.sitreps!.map((sr) => (
                  <div key={sr.id} className="rounded border border-slate-100 px-2.5 py-1.5 text-xs">
                    <div className="mb-0.5 text-slate-400">{formatDateTime(sr.occurredAt)} — {sr.authoredBy}</div>
                    {sr.narrative}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function TaskingRow({ t, busy, onAdvance }: { t: Tasking; busy: boolean; onAdvance: (status: TaskingStatus) => void }) {
  const next = NEXT_TASKING_STATUS[t.status];
  return (
    <div className="rounded border border-slate-100 px-2.5 py-1.5 text-xs">
      <div className="flex items-center justify-between">
        <span className="font-semibold">{t.agency}</span>
        <StatusBadge status={t.status} />
      </div>
      <div className="text-slate-500">{t.instructions}</div>
      {next.length > 0 ? (
        <div className="mt-1 flex gap-1">
          {next.map((n) => (
            <button key={n} className={n === 'CANCELLED' ? 'btn-danger !px-1.5 !py-0.5 text-[11px]' : 'btn-secondary !px-1.5 !py-0.5 text-[11px]'} disabled={busy} onClick={() => onAdvance(n)}>
              {n}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CreateIncidentModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [hazard, setHazard] = useState<HazardType>('CYCLONE');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m36/incidents', { title, hazard, summary });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open incident');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Open EOC incident" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Hazard">
          <select className="field-input" value={hazard} onChange={(e) => setHazard(e.target.value as HazardType)}>
            {HAZARDS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Summary">
          <textarea className="field-input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || title.trim().length < 3 || summary.trim().length < 10} onClick={submit}>
            {busy ? 'Opening…' : 'Open incident'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function TaskingsTab({ isEoc }: { isEoc: boolean }) {
  const [status, setStatus] = useState('');
  const [agency, setAgency] = useState('');
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '100' });
    if (status) params.set('status', status);
    if (agency) params.set('agency', agency);
    return `/api/v1/m36/taskings?${params.toString()}`;
  }, [status, agency]);
  const listQ = useApiQuery<Page<Tasking>>(isEoc ? path : null);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <input className="field-input !w-56" placeholder="Filter by agency…" value={agency} onChange={(e) => setAgency(e.target.value)} />
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {TASKING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No taskings match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Agency</th>
                <th className="table-head">Instructions</th>
                <th className="table-head">Due</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((t) => (
                <tr key={t.id}>
                  <td className="table-cell font-medium">{t.agency}</td>
                  <td className="table-cell text-xs text-slate-500">{t.instructions}</td>
                  <td className="table-cell text-xs">{t.dueAt ? formatDateTime(t.dueAt) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={t.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-400">Open a tasking's parent incident from the Incidents tab to acknowledge/advance/cancel it.</p>
    </div>
  );
}
